// Der Datenvertrag zwischen Apps und Server: Was über die Leitung geht, wird
// hier einmal beschrieben und auf beiden Seiten gleich geprüft (PLAN.md,
// Abschnitt 3, „Sprache“). Gebaut ist in E1 nur, was Anmeldung und
// Verwaltung brauchen; Kunde, Objekt und Aufnahme folgen in E2
// (aufnahme-vertrag.ts), das Büro in E3 (buero-vertrag.ts), dazu seit E3
// die Rechte je Rolle (rechte.ts).

import * as z from "zod";

/** Welche App eine Anfrage stellt – je Rolle eine Unteradresse. */
export const APPS = ["doku", "crm"] as const;
export type AppName = (typeof APPS)[number];

/**
 * Die Rollen. Bis E3 waren sie nur ein Feld, alle Angemeldeten sahen alles;
 * seit E3 (Gernot, 21.10.2026) zählt die Rolle: Jede Rolle sieht ihre
 * Sache, die Leitung (neu, Manuel) alles. Eine Rolle je Person. Was welche
 * Rolle darf, steht an EINER Stelle: rechte.ts. Die Rolle Leitung gilt je
 * Firma – die Ebene Happy Energy über allen Firmen (Franchise) ist sie
 * nicht (PLAN.md, E3, „Franchise“).
 */
export const ROLLEN = ["leitung", "buero", "verkauf", "lager", "montage", "inbetriebnahme"] as const;
export type Rolle = (typeof ROLLEN)[number];

/**
 * Die Rollen, die man vergeben kann – beim Einladen und beim Ändern der
 * Rolle. Seit E3c ohne das Lager (Gernot, 2.11.2026; H26: „Es wird kein
 * großes Lager mehr geben“): Der Wert bleibt in ROLLEN und in der Prüfung der
 * Datenbank, niemand hat ihn (Migration 0009 bricht ab, wenn doch), und die
 * Rolle darf nichts mehr (rechte.ts).
 */
export const VERGEBBARE_ROLLEN = ["leitung", "buero", "verkauf", "montage", "inbetriebnahme"] as const satisfies readonly Rolle[];
export type VergebbareRolle = (typeof VERGEBBARE_ROLLEN)[number];

export const ROLLEN_NAMEN: Record<Rolle, string> = {
  leitung: "Leitung",
  buero: "Büro",
  verkauf: "Verkauf",
  lager: "Lager",
  montage: "Montage",
  inbetriebnahme: "Inbetriebnahme",
};

export const GERAETE_STAENDE = ["wartet", "freigegeben", "gesperrt"] as const;
export type GeraeteStand = (typeof GERAETE_STAENDE)[number];

/** Ein Name, wie ihn Menschen tippen: getrimmt, nicht leer, nicht endlos. */
const name = z.string().trim().min(1, "Bitte einen Namen eingeben.").max(120);

export const neuerNutzerEingabe = z.object({
  name,
  email: z.email("Bitte eine gültige E-Mail-Adresse eingeben.").trim().toLowerCase().max(200),
  rolle: z.enum(VERGEBBARE_ROLLEN),
});
export type NeuerNutzerEingabe = z.infer<typeof neuerNutzerEingabe>;

/** Die Rolle eines anderen ändern (POST /api/nutzer/:id/rolle) – nur die Leitung (Recht „rollen-vergeben“). */
export const rolleAendernEingabe = z.object({
  rolle: z.enum(VERGEBBARE_ROLLEN),
});
export type RolleAendernEingabe = z.infer<typeof rolleAendernEingabe>;

export const einladungPruefenEingabe = z.object({
  code: z.string().min(1).max(40),
});

export const geraetNameEingabe = z.object({
  name: z.string().trim().min(1).max(80),
});

/** Was die App nach dem Anmelden über sich erfährt (GET /api/ich). */
export interface IchAntwort {
  nutzer: { id: string; name: string; email: string; rolle: Rolle };
  firma: { id: string; name: string };
  geraet: { id: string; name: string; app: AppName; stand: GeraeteStand };
  umgebung: Umgebung;
}

/** Warum der Server eine Anfrage abweist – die App zeigt dazu den passenden Bildschirm. */
export const ABWEISUNGSGRUENDE = [
  "nicht-angemeldet",
  "geraet-wartet",
  "geraet-gesperrt",
  "nutzer-gesperrt",
  "kein-passkey",
  "falsche-app",
  /** Seit E3: angemeldet, richtige App – aber die Rolle darf das nicht (rechte.ts). */
  "keine-berechtigung",
] as const;
export type Abweisungsgrund = (typeof ABWEISUNGSGRUENDE)[number];

export interface Abweisung {
  grund: Abweisungsgrund;
  meldung: string;
}

export const UMGEBUNGEN = ["entwicklung", "test", "echt"] as const;
export type Umgebung = (typeof UMGEBUNGEN)[number];

export interface GeraetZeile {
  id: string;
  nutzer: { id: string; name: string };
  name: string;
  app: AppName;
  stand: GeraeteStand;
  standGeaendertAm: string | null;
  standGeaendertVon: string | null;
  zuletztGesehen: string | null;
  angelegtAm: string;
}

export interface NutzerZeile {
  id: string;
  name: string;
  email: string;
  rolle: Rolle;
  aktiv: boolean;
  passkeys: number;
  offeneEinladung: { gueltigBis: string } | null;
}

export interface EinladungAntwort {
  nutzerId: string;
  name: string;
  /** Nur jetzt sichtbar – der Server speichert ihn nicht lesbar. */
  code: string;
  gueltigBis: string;
}
